import React, { useEffect, useRef } from 'react';
import { oktaAuthFor, setLoginTenant, tenantForIssuer } from '../authConfig';

// Relay landing for Okta IdP-initiated SSO. This route is the "Initiate Login URI"
// registered on the Okta app, in both tenants during the migration.
//
// IdP-initiated SSO (the Okta dashboard tile) drops users on the app without
// the PKCE state that handleLoginRedirect() needs, so it can never seat an
// access token in oktaAuth.tokenManager — and franchiseApi's getAuthHeader()
// then omits the Authorization header on v2 calls.
//
// The fix is to make the tile point at this route, which immediately starts a
// proper SP-initiated PKCE flow. Okta already has a live session, so it bounces
// straight back through /login/callback (no second prompt), where
// handleLoginRedirect() stores both the idToken and accessToken normally.
//
// Which tenant that flow runs against comes from the `iss` Okta sends with the launch, so a
// tile on either tenant's dashboard lands its user in the right directory. An absent or
// unrecognised `iss` falls back to primary, the tenant logins start at.
const OktaLogin: React.FC = () => {
  const started = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in dev; a second signInWithRedirect would overwrite the
    // first flow's PKCE transaction mid-redirect.
    if (started.current) return;
    started.current = true;

    const params = new URLSearchParams(window.location.search);
    const tenant = tenantForIssuer(params.get('iss')) ?? 'primary';
    const loginHint = params.get('login_hint') || undefined;

    // Record the tenant before leaving: /login/callback loads fresh and has only this marker
    // to tell it which tenant issued the code it is about to redeem.
    setLoginTenant(tenant);
    oktaAuthFor(tenant)
      .signInWithRedirect(loginHint ? { loginHint } : undefined)
      .catch(() => {
        // The flow couldn't start (e.g. the idp tenant isn't configured in this build) —
        // fall back to the normal login screen rather than stranding the user here.
        window.location.replace('/');
      });
  }, []);

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: '#0a202e',
      color: '#20cbd4'
    }}>
      <div>
        <h2>Signing in...</h2>
        <p>Please wait while we redirect you to Okta.</p>
      </div>
    </div>
  );
};

export default OktaLogin;
